import React from 'react';

import { Box, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import _ from 'lodash';

import { CITATION_INLINE_MARKER_REGEX } from 'constants/aiChat';

import { findCitationByKey, splitCitationSegments } from '../citationMarkers.utils';

const BOLD_MARKER_LENGTH = -2;
const BOLD_REGEX = /(\*\*[^*]+\*\*)/g;
const CITATION_LINE_REGEX = /^\[\^cite:([^\]]+)\]$/;
const INLINE_CITATION_STRIP_REGEX = /\s*\[\^cite:[^\]]+\]\s*/g;
const MARKDOWN_HEADING_REGEX = /^(#{1,6})\s+(.+)$/;
const MAX_HEADING_LEVEL = 6;

const getCitationKey = citation => citation?.citationId ?? citation?.id ?? null;

const stripInlineCitationMarkers = text => text.replace(INLINE_CITATION_STRIP_REGEX, ' ').trim();

const buildFragmentKey = (kind, position, content) => `${kind}-${position}-${content}`;

const buildExpanderContentPropsFromCitation = citation => ({
  citationId: getCitationKey(citation),
  sectionId: citation.sectionId ?? null,
  sourceId: citation.sourceId ?? null,
  sourceName: citation.sourceName ?? '',
  sourceShortName: citation.sourceShortName ?? '',
  sectionNumber: citation.sectionNumber ?? '',
  sectionTitle: citation.sectionTitle ?? citation.title ?? '',
  excerpt: citation.excerpt ?? citation.snippet ?? '',
});

const parseBoldFormatting = (text, keyPrefix) => {
  const parts = text
    .split(BOLD_REGEX)
    .map((part, idx) => ({ part, key: buildFragmentKey(keyPrefix, idx, part) }));

  return parts
    .map(({ part, key }) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return (
          <Box className="ai-highlight" component="span" key={key}>
            {part.slice(2, BOLD_MARKER_LENGTH)}
          </Box>
        );
      }

      return part;
    })
    .filter(part => !_.isNil(part) && part !== '');
};

// LEN-213: when the caller supplies an `InlineCitation` component (AI Activity), an
// inline marker — mid-sentence, in a list item, or in a table cell such as a
// guideline-checks "citation" column — renders as a citation chip in place. Without
// one (AI chat) inline markers are stripped exactly as before.
const parseInlineFormatting = (text, inlineOptions) => {
  const InlineCitation = inlineOptions?.InlineCitation ?? null;

  if (_.isNil(InlineCitation)) {
    const cleaned = stripInlineCitationMarkers(text);

    return cleaned.length ? parseBoldFormatting(cleaned, 'inline') : [];
  }

  return splitCitationSegments(text).flatMap((segment, idx) => {
    if (segment.type === 'citation') {
      return [
        <InlineCitation
          citation={findCitationByKey(inlineOptions.citations ?? [], segment.key)}
          citationKey={segment.key}
          key={buildFragmentKey('inline-citation', idx, segment.key)}
          status={inlineOptions.citationStatus ?? null}
        />,
      ];
    }

    return parseBoldFormatting(segment.value, `inline-${idx}`);
  });
};

const processTableLine = (trimmedLine, state) => {
  const cells = trimmedLine
    .slice(1, -1)
    .split('|')
    .map(c => c.trim());

  if (cells.every(c => /^[-:]+$/.test(c))) {
    return state;
  }

  if (!state.inTable) {
    return {
      ...state,
      inTable: true,
      tableHeaders: cells,
    };
  }

  return {
    ...state,
    tableRows: [...state.tableRows, cells],
  };
};

const processListLine = (trimmedLine, state) => {
  const listItem = trimmedLine.replace(/^(\d+\.\s|-\s)/, '');

  return {
    ...state,
    currentList: [...state.currentList, listItem],
  };
};

const processHeaderLine = (trimmedLine, lineIdx, state) => ({
  ...state,
  elements: [
    ...state.elements,
    <Box className="ai-section-header" component="span" key={`header-${lineIdx}`}>
      {trimmedLine.slice(2, BOLD_MARKER_LENGTH)}
    </Box>,
  ],
});

const processMarkdownHeadingLine = (headingMatch, lineIdx, state) => {
  const level = Math.min(headingMatch[1].length, MAX_HEADING_LEVEL);
  const text = headingMatch[2].trim();

  return {
    ...state,
    elements: [
      ...state.elements,
      <Box
        className={`ai-section-header ai-section-header-h${level}`}
        component={`h${level}`}
        key={`heading-${lineIdx}`}
      >
        {parseInlineFormatting(text, state.inlineOptions)}
      </Box>,
    ],
  };
};

const buildParagraphElement = (trimmedLine, lineIdx, inlineOptions) => (
  <Box className="ai-paragraph" component="p" key={`p-${lineIdx}`}>
    {parseInlineFormatting(trimmedLine, inlineOptions)}
  </Box>
);

const buildCitationDescriptionElement = (trimmedLine, lineIdx, inlineOptions) => (
  <Box className="ai-paragraph ai-citation-description" component="p" key={`p-${lineIdx}`}>
    {parseInlineFormatting(trimmedLine, inlineOptions)}
  </Box>
);

const processParagraphLine = (trimmedLine, lineIdx, state) => {
  if (!_.isNil(state.pendingCitation)) {
    return {
      ...state,
      pendingCitation: {
        ...state.pendingCitation,
        descriptionElements: [
          ...state.pendingCitation.descriptionElements,
          buildCitationDescriptionElement(trimmedLine, lineIdx, state.inlineOptions),
        ],
      },
    };
  }

  return {
    ...state,
    elements: [...state.elements, buildParagraphElement(trimmedLine, lineIdx, state.inlineOptions)],
  };
};

const flushListToElements = state => {
  if (!state.currentList.length) {
    return state;
  }

  return {
    ...state,
    elements: [
      ...state.elements,
      <Box className="ai-list" component="ul" key={`list-${state.elements.length}`}>
        {state.currentList
          .map((item, idx) => ({ item, key: buildFragmentKey('list-item', idx, item) }))
          .map(({ item, key }) => (
            <Box className="ai-list-item" component="li" key={key}>
              {parseInlineFormatting(item, state.inlineOptions)}
            </Box>
          ))}
      </Box>,
    ],
    currentList: [],
  };
};

const flushTableToElements = state => {
  if (!state.tableRows.length && !state.tableHeaders.length) {
    return state;
  }

  return {
    ...state,
    elements: [
      ...state.elements,
      <Table className="ai-table" key={`table-${state.elements.length}`}>
        {!!state.tableHeaders.length && (
          <TableHead>
            <TableRow>
              {state.tableHeaders
                .map((header, idx) => ({
                  header,
                  key: buildFragmentKey('table-header', idx, header),
                }))
                .map(({ header, key }) => (
                  <TableCell key={key}>{header.trim()}</TableCell>
                ))}
            </TableRow>
          </TableHead>
        )}
        <TableBody>
          {state.tableRows
            .map((row, rowIdx) => ({
              cells: row.map((cell, cellIdx) => ({
                cell,
                key: buildFragmentKey('table-cell', `${rowIdx}-${cellIdx}`, cell),
              })),
              key: buildFragmentKey('table-row', rowIdx, row.join('|')),
            }))
            .map(({ cells, key }) => (
              <TableRow key={key}>
                {cells.map(({ cell, key: cellKey }) => (
                  <TableCell key={cellKey}>
                    {parseInlineFormatting(cell.trim(), state.inlineOptions)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
        </TableBody>
      </Table>,
    ],
    tableRows: [],
    tableHeaders: [],
    inTable: false,
  };
};

const flushPendingCitation = state => {
  if (_.isNil(state.pendingCitation)) {
    return state;
  }

  const { lineIdx, citationId, contentProps, descriptionElements } = state.pendingCitation;
  const CitationCard = state.inlineOptions.CitationCard ?? null;

  return {
    ...state,
    elements: [
      ...state.elements,
      <Box className="ai-citation-card" key={`citation-${lineIdx}-${citationId}`}>
        {!_.isNil(CitationCard) && (
          <CitationCard
            contentProps={contentProps}
            status={state.inlineOptions.citationStatus ?? null}
          />
        )}
      </Box>,
      ...descriptionElements,
    ],
    pendingCitation: null,
  };
};

const processCitationLine = (citationId, lineIdx, state) => {
  if (state.renderedCitationIds.has(citationId)) {
    return flushPendingCitation(state);
  }

  const citation = findCitationByKey(state.inlineOptions.citations ?? [], citationId);

  const flushedState = flushPendingCitation(state);
  const contentProps = citation ? buildExpanderContentPropsFromCitation(citation) : { citationId };
  const nextRenderedIds = new Set(flushedState.renderedCitationIds);

  nextRenderedIds.add(citationId);

  return {
    ...flushedState,
    pendingCitation: {
      lineIdx,
      citationId,
      contentProps,
      descriptionElements: [],
    },
    renderedCitationIds: nextRenderedIds,
  };
};

const processLine = (line, lineIdx, state) => {
  const trimmedLine = line.trim();

  if (trimmedLine.startsWith('|') && trimmedLine.endsWith('|')) {
    const citationFlushedState = flushPendingCitation(state);
    const flushedState = flushListToElements(citationFlushedState);

    return processTableLine(trimmedLine, flushedState);
  }

  const tableState = state.inTable ? flushTableToElements(state) : state;

  const citationLineMatch = CITATION_LINE_REGEX.exec(trimmedLine);

  if (!_.isNil(citationLineMatch)) {
    const listFlushedState = flushListToElements(tableState);

    return processCitationLine(citationLineMatch[1], lineIdx, listFlushedState);
  }

  if (/^\d+\.\s/.test(trimmedLine) || trimmedLine.startsWith('- ')) {
    const citationFlushedState = flushPendingCitation(tableState);

    return processListLine(trimmedLine, citationFlushedState);
  }

  const listState = flushListToElements(tableState);

  const headingMatch = MARKDOWN_HEADING_REGEX.exec(trimmedLine);

  if (!_.isNil(headingMatch)) {
    const citationFlushedState = flushPendingCitation(listState);

    return processMarkdownHeadingLine(headingMatch, lineIdx, citationFlushedState);
  }

  if (trimmedLine.startsWith('**') && trimmedLine.endsWith('**')) {
    const citationFlushedState = flushPendingCitation(listState);

    return processHeaderLine(trimmedLine, lineIdx, citationFlushedState);
  }

  if (trimmedLine) {
    return processParagraphLine(trimmedLine, lineIdx, listState);
  }

  return listState;
};

const splitCitationsInLines = lines =>
  lines.reduce((result, line) => {
    if (!line.includes('[^cite:')) {
      return [...result, line];
    }

    const parts = line.split(/(\[\^cite:[^\]]+\])/g);
    const initialSplitState = { subLines: [], currentLineParts: [] };

    const finalSplitState = parts.reduce((state, part, index) => {
      if (index % 2 === 0 && part === '') {
        return state;
      }

      if (index % 2 === 0) {
        return {
          ...state,
          currentLineParts: [...state.currentLineParts, part],
        };
      }

      const precedingText = parts.slice(0, index).join('').trim();

      const succeedingText = parts
        .slice(index + 1)
        .join('')
        .trim();

      if (precedingText !== '' && succeedingText !== '') {
        return {
          ...state,
          currentLineParts: [...state.currentLineParts, part],
        };
      }

      const nextSubLines = [...state.subLines];

      if (state.currentLineParts.length > 0) {
        nextSubLines.push(state.currentLineParts.join(''));
      }

      nextSubLines.push(part);

      return {
        subLines: nextSubLines,
        currentLineParts: [],
      };
    }, initialSplitState);

    const finalSubLines = [...finalSplitState.subLines];

    if (finalSplitState.currentLineParts.length > 0) {
      finalSubLines.push(finalSplitState.currentLineParts.join(''));
    }

    return [...result, ...finalSubLines];
  }, []);

export const parseFormattedContent = (content, inlineOptions) => {
  if (!content) {
    return null;
  }

  const lines = splitCitationsInLines(content.split('\n'));
  const initialState = {
    elements: [],
    currentList: [],
    inTable: false,
    tableRows: [],
    tableHeaders: [],
    inlineOptions: inlineOptions ?? {},
    renderedCitationIds: new Set(),
    pendingCitation: null,
  };

  const finalState = lines.reduce(
    (state, line, lineIdx) => processLine(line, lineIdx, state),
    initialState,
  );

  const flushedListState = flushListToElements(finalState);
  const flushedTableState = flushTableToElements(flushedListState);
  const flushedCitationState = flushPendingCitation(flushedTableState);

  return flushedCitationState.elements;
};

export const extractCitationIdsFromContent = content => {
  if (!_.isString(content) || !content.length) {
    return [];
  }

  const matches = content.matchAll(CITATION_INLINE_MARKER_REGEX);
  const ids = [];

  Array.from(matches).forEach(match => {
    const citationId = match[1];

    if (!ids.includes(citationId)) {
      ids.push(citationId);
    }
  });

  return ids;
};
